import React, { useState } from "react";
import {
  useCreateGameTopic,
  useCreateTopicMessage,
  useTopicMessages,
  useUpdatePlayTest,
  useUpdateTopicMessage,
} from "@seabrookstudios/pitch2table";
import { SmartTextField } from "../core/SmartTextField";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Edit from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import { ThisUserAvatar } from "../users/UserAvatar";
import CardHeader from "@mui/material/CardHeader";
import { formatTopicDate } from "./model";
import { Editor } from "../core/Editor";
import DeleteForever from "@mui/icons-material/DeleteForever";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import { Message } from "./Message";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import { TopicTypeSelect } from "./TopicTypeSelect";
import IntegrationInstructions from "@mui/icons-material/IntegrationInstructions";

/**
 * @param {object} props
 * @param {string} props.id
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const AddTopicToGame = ({ gameId, id }) => {
  const createGameTopic = useCreateGameTopic(gameId);

  const [name, setName] = useState("");
  const [type, setType] = useState(/** @type {import("@seabrookstudios/pitch2table-core").TopicType} */ ("General"));
  const [body, setBody] = useState("");

  const onSaveHandler = async () => {
    await createGameTopic.mutateAsync({
      name,
      body,
      type,
      parent: undefined,
      parentId: undefined,
      communal: false,
      implementation: "Markdown",
    });
  };

  return (
    <FullScreenDialog
      id={id}
      title="New Topic"
      actionText="SAVE"
      onSaveHandler={onSaveHandler}
      canSave={!name}
      isBusy={createGameTopic.isPending}
    >
      <Box>
        <TopicTypeSelect
          value={type}
          onChange={setType}
          helperText="A broad category for this topic"
          isPending={createGameTopic.isPending}
        />
        <SmartTextField
          id="topic-title"
          label="Title"
          initialValue={name}
          onChange={setName}
          multiline={false}
          borderless
          helperText="Topic Title"
        />
        <Editor initialValue={body} onChange={setBody} implementation="Markdown" />
      </Box>
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {import("@seabrookstudios/pitch2table-core").MessageId} props.messageId
 * @param {string} props.initialBody
 * @param {import("@seabrookstudios/pitch2table-core").EditorImplementations} props.implementation
 * @param {boolean} props.disabled
 * @param {boolean} props.deleted
 */
export const EditMessage = ({ topicId, messageId, deleted, initialBody, disabled, implementation = "Markdown" }) => {
  const updateTopicMessage = useUpdateTopicMessage(topicId, messageId);

  const [body, setBody] = useState(initialBody);

  const onSaveHandlerMarkdown = async () => {
    await updateTopicMessage.mutateAsync({
      body,
      deleted: undefined,
      implementation: "Markdown",
    });

    setBody(body);
  };

  const deleteMessage = async () => {
    await updateTopicMessage.mutateAsync({
      body: undefined,
      deleted: true,
      implementation,
    });
  };

  const restoreMessage = async () => {
    await updateTopicMessage.mutateAsync({
      body: undefined,
      deleted: false,
      implementation,
    });
  };

  return (
    <FullScreenDialog
      title="Update Message"
      actionText="SAVE"
      onSaveHandler={onSaveHandlerMarkdown}
      onDeleteHandler={deleted ? restoreMessage : deleteMessage}
      canSave={false}
      isBusy={updateTopicMessage.isPending}
      Icon={Edit}
      disabled={disabled || deleted}
      DeleteIcon={deleted ? <RestoreFromTrash /> : <DeleteForever />}
      minWidth="66ch"
    >
      <Editor implementation={implementation} initialValue={body} onChange={setBody} />
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Message[]} props.messages
 * @param {string|undefined} [props.tooltip]
 */
export const PopUpMessages = ({ messages = [], tooltip }) => {
  return (
    <FullScreenDialog
      title="Print & Play Instructions"
      canSave={true}
      Icon={IntegrationInstructions}
      disabled={messages.length === 0}
      tooltip={tooltip}
    >
      {messages.map((message, i) => (
        <Message key={message.id} message={message} isFirst={i === 0} readOnly />
      ))}
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {string|undefined} [props.tooltip]
 */
export const PopUpTopic = ({ topicId, tooltip }) => {
  const { data: messages = [] } = useTopicMessages(topicId);

  return (
    <FullScreenDialog title="Pitch Deck" canSave={true} Icon={CoPresentIcon} disabled={!topicId} tooltip={tooltip}>
      {messages.map((message, i) => (
        <Message key={message.id} message={message} isFirst={i === 0} readOnly />
      ))}
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {string} props.name
 * @param {import("@seabrookstudios/pitch2table-core").EditorImplementations} props.implementation
 */
export const AddMessageToTopic = ({ name, topicId, implementation }) => {
  const createTopicMessage = useCreateTopicMessage(topicId);

  const [visible, setVisible] = useState(false);
  const [body, setBody] = useState("");

  const onSaveHandler = async () => {
    await createTopicMessage.mutateAsync({
      body,
      implementation: "Markdown",
    });

    setBody("");
    setVisible(false);
  };

  return (
    <Card>
      {visible ? (
        <Box p={1}>
          <CardHeader avatar={<ThisUserAvatar />} title={`In response to ${name}`} subheader={formatTopicDate()} />
          <Box p={1} id="comment">
            <Editor implementation={implementation} initialValue={""} onChange={setBody} />
          </Box>
          <CardActions>
            <Button disabled={body.length === 0} onClick={onSaveHandler}>
              Add comment
            </Button>
          </CardActions>
        </Box>
      ) : (
        <Box p={1} onClick={() => setVisible(true)}>
          <CardHeader avatar={<ThisUserAvatar />} title="Add a comment here..." subheader={formatTopicDate()} />
        </Box>
      )}
    </Card>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 */
export const AddMessageToPlayTest = ({ gameId, playTestId }) => {
  const updatePlayTest = useUpdatePlayTest(gameId, playTestId);

  const [visible, setVisible] = useState(false);
  const [body, setBody] = useState("");

  const onSaveHandler = async () => {
    await updatePlayTest.mutateAsync({
      body: /** @type {string} */ (body),
      audience: undefined,
      complete: undefined,
      date: undefined,
      duration: undefined,
      ignore: undefined,
      medium: undefined,
      rulesVersion: undefined,
      nonPlayerOutcomes: undefined,
      outcomes: undefined,
      timerRunning: undefined,
      timerStarted: undefined,
      implementation: "Markdown",
      publisherId: undefined,
    });

    setBody("");
    setVisible(false);
  };

  return (
    <Card>
      {visible ? (
        <Box p={1}>
          <Box p={1} id="comment">
            <Editor initialValue={body} onChange={setBody} implementation={"Markdown"} />
          </Box>
          <CardActions>
            <Button disabled={body.length === 0} onClick={onSaveHandler}>
              Add comment
            </Button>
          </CardActions>
        </Box>
      ) : (
        <Box p={1} onClick={() => setVisible(true)}>
          <CardHeader avatar={<ThisUserAvatar />} title="Add a comment here..." subheader={formatTopicDate()} />
        </Box>
      )}
    </Card>
  );
};
